import {useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import produce from "immer";

import {UPCOMING_ORDERS} from "@globals/DailyOrders/interfaces";
import {CHECKOUT_SOURCE_PAGE} from "@globals/constants/localStorageKeys";
import {CheckoutResponse} from "@globals/types/CheckoutResponse";
import {ExtendedError} from "@globals/types/ExtendedError";
import {DateString} from "@globals/types/utilityTypes";

import {
  CreatePartialOrderParams,
  checkout,
  createPartialOrder,
  editOrderItem,
  getPartialOrders,
} from "../DailyMenuService";
import {
  AddDailyItemData,
  ChangeDailyItemData,
  DailyOrderStatus,
  DailyOrdersResource,
  EditDailyItemParams,
  ErrorTitle,
  PARTIAL_ORDERS,
  PartialOrder,
} from "../interfaces";
import useDailyMenu from "./useDailyMenu";

const mutationKey = "dailyMutation";

export default function useDailyOrders(): DailyOrdersResource {
  const queryClient = useQueryClient();
  const {selectedDate, selectedLocationId} = useDailyMenu();
  const {
    data: partialOrders,
    isPending: ordersLoading,
    isFetched,
    refetch,
  } = useQuery<PartialOrder[], Error>({
    queryKey: [PARTIAL_ORDERS],
    queryFn: getPartialOrders,
  });
  const [latestError, setLatestError] = useState<ExtendedError>();

  // MUTATIONS
  const onOrderMutationError = (error: ExtendedError) => {
    setLatestError(error);
    refetch();
    if (error.title === ErrorTitle.ItemSoldOut) {
      // re-fetch menu to remove sold out item
      queryClient.invalidateQueries({queryKey: ["menu"]});
    }
  };

  const createOrderMutation = useMutation<
    PartialOrder,
    Error,
    CreatePartialOrderParams
  >({
    mutationFn: createPartialOrder,
    mutationKey: [mutationKey, "createOrder"],
    onSuccess: (createdOrder) => {
      queryClient.setQueryData([PARTIAL_ORDERS], (oldData: PartialOrder[]) =>
        produce(oldData, (draft) => {
          draft.push(createdOrder);
        })
      );
    },
    onError: onOrderMutationError,
  });

  const checkoutMutation = useMutation<CheckoutResponse, Error, void>({
    mutationFn: checkout,
    mutationKey: ["dailyMutation", "checkout"],
    onSuccess: () => {
      refetch();
      queryClient.invalidateQueries({queryKey: [UPCOMING_ORDERS]});
    },
    onError: (error: ExtendedError) => {
      if (error.status === 402) {
        // payment required;
        // remember current page to return here if cart expires;
        localStorage.setItem(
          CHECKOUT_SOURCE_PAGE,
          window.location.pathname + window.location.search
        );
        window.location.assign("/checkout");
      } else {
        onOrderMutationError(error);
      }
    },
  });

  const onOrderMutationSuccess = (updatedOrder: PartialOrder) => {
    queryClient.setQueryData([PARTIAL_ORDERS], (oldData: PartialOrder[]) =>
      produce(oldData, (draft) => {
        const index = draft.findIndex((order) => order.id === updatedOrder.id);
        if (updatedOrder.order_status === DailyOrderStatus.Cancelled) {
          // remove order if its cancelled;
          draft.splice(index, 1);
        } else {
          draft[index] = updatedOrder;
        }
      })
    );
  };

  const editItemMutation = useMutation<
    PartialOrder,
    Error,
    EditDailyItemParams
  >({
    mutationFn: (variables) => editOrderItem(variables),
    mutationKey: [mutationKey, "editItem"],
    onSuccess: onOrderMutationSuccess,
    onError: onOrderMutationError,
  });

  const isLoading =
    (isFetched && ordersLoading) ||
    editItemMutation.isPending ||
    createOrderMutation.isPending ||
    checkoutMutation.isPending;

  // Adding item in the cart;
  const createOrderWithItem = (itemData: AddDailyItemData) => {
    return createOrderMutation
      .mutateAsync({
        date: itemData.deliveryDate as DateString,
        locationId: itemData.locationId,
      })
      .then(({id}) => {
        return editItemMutation.mutateAsync({
          item_id: itemData.item.id,
          count: 1,
          orderId: id,
        });
      });
  };

  // Editing item in the cart;
  // We can change qty in different orders;
  const handleItemUpdate = (itemData: ChangeDailyItemData) => {
    return editItemMutation.mutateAsync({
      item_id: itemData.item.id,
      count: itemData.quantity,
      orderId: itemData.orderId,
      special_instructions: itemData.specialInstructions,
    });
  };

  // Adding item from the menu;
  // We can add only +1
  const addItem = (itemData: AddDailyItemData) => {
    if (
      ordersLoading ||
      editItemMutation.isPending ||
      createOrderMutation.isPending
    ) {
      // prevent extra requests (instead of debouncing);
      return Promise.reject("loading");
    }
    const existingOrder = partialOrders?.find(
      (order) =>
        order.delivery_date ==
          ((itemData.deliveryDate ?? selectedDate) as DateString) &&
        (order.location_id === selectedLocationId ||
          createOrderMutation.data?.is_location_migrated)
    );
    if (existingOrder) {
      const existingQty =
        existingOrder?.order_items.find((item) => item.id === itemData.item.id)
          ?.count || 0;
      return handleItemUpdate({
        item: itemData.item,
        quantity: existingQty + 1,
        orderId: existingOrder.id,
        deliveryDate: itemData.deliveryDate,
      });
    } else {
      return createOrderWithItem({
        item: itemData.item,
        deliveryDate: (itemData.deliveryDate ?? selectedDate) as DateString,
        locationId: selectedLocationId,
      });
    }
  };

  const resetOrder = (): void => {
    queryClient.setQueryData([PARTIAL_ORDERS], []);
  };

  return {
    partialOrders,
    addItem,
    handleItemUpdate,
    isLoading,
    checkout: checkoutMutation.mutateAsync,
    latestError: latestError,
    resetOrder,
    createOrderMutation,
  };
}
